import api from '../config/api';
import { ENV_CONFIG } from '../config/environment';
import { resolverUrlArchivo as armarUrlArchivo } from '../utils/resolverUrlArchivo';

const resolverUrlArchivo = (url) => armarUrlArchivo(url, ENV_CONFIG.API_BASE_URL);

const expedienteLaboralService = {
  async obtenerPorEmpleado(empleadoId) {
    try {
      const response = await api.get(`/expediente-laboral/empleado/${empleadoId}`);
      const data = response.data;
      if (data?.success && data?.data?.documentos) {
        Object.keys(data.data.documentos).forEach((tipo) => {
          const doc = data.data.documentos[tipo];
          if (doc) doc.archivo_url = resolverUrlArchivo(doc.archivo_url);
        });
        (data.data.historial_antecedentes || []).forEach((doc) => {
          doc.archivo_url = resolverUrlArchivo(doc.archivo_url);
        });
      }
      return data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Error al obtener el expediente laboral',
      };
    }
  },

  async subirDocumento(empleadoId, { tipoDocumento, archivo, documentado, fechaEmision }) {
    try {
      const formData = new FormData();
      formData.append('tipo_documento', tipoDocumento);
      formData.append('archivo', archivo);
      if (documentado !== undefined && documentado !== null) {
        formData.append('documentado', documentado ? '1' : '0');
      }
      if (fechaEmision) {
        formData.append('fecha_emision', fechaEmision);
      }

      const response = await api.post(`/expediente-laboral/empleado/${empleadoId}`, formData, {
        headers: { 'Content-Type': undefined },
        transformRequest: [(data, headers) => {
          if (headers && typeof headers === 'object') {
            delete headers['Content-Type'];
          }
          return data;
        }],
      });
      const data = response.data;
      if (data?.data?.archivo_url) {
        data.data.archivo_url = resolverUrlArchivo(data.data.archivo_url);
      }
      return data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Error al subir el documento',
        details: error.response?.data?.details,
      };
    }
  },

  async actualizarMetadatos(documentoId, { documentado, fechaEmision }) {
    try {
      const payload = {};
      if (documentado !== undefined) payload.documentado = Boolean(documentado);
      if (fechaEmision) payload.fecha_emision = fechaEmision;

      const response = await api.put(`/expediente-laboral/documento/${documentoId}`, payload);
      const data = response.data;
      if (data?.data?.archivo_url) {
        data.data.archivo_url = resolverUrlArchivo(data.data.archivo_url);
      }
      return data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Error al actualizar el documento',
      };
    }
  },

  async eliminarDocumento(documentoId) {
    try {
      const response = await api.delete(`/expediente-laboral/documento/${documentoId}`);
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Error al eliminar el documento',
      };
    }
  },

  urlDescarga(documentoId) {
    const base = ENV_CONFIG.API_BASE_URL.replace(/\/$/, '');
    return `${base}/expediente-laboral/documento/${documentoId}/descargar`;
  },

  async abrirDocumento(documentoId, nombre) {
    try {
      const response = await api.get(`/expediente-laboral/documento/${documentoId}/descargar`, {
        responseType: 'blob',
      });

      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const texto = await response.data.text();
        const parsed = JSON.parse(texto);
        return {
          success: false,
          error: parsed.error || 'No se pudo abrir el documento',
        };
      }

      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const mime = (blob.type || contentType).toLowerCase();
      const sePuedeVer = mime.includes('pdf') || mime.startsWith('image/');
      const win = sePuedeVer ? window.open(url, '_blank', 'noopener,noreferrer') : null;

      if (!win) {
        const link = document.createElement('a');
        link.href = url;
        link.download = nombre || 'documento';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
      return { success: true };
    } catch (error) {
      let mensaje = 'Error al abrir el documento';
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const texto = await data.text();
          const parsed = JSON.parse(texto);
          mensaje = parsed.error || mensaje;
        } catch (_) {
          // ignore
        }
      } else if (error.response?.data?.error) {
        mensaje = error.response.data.error;
      }
      return { success: false, error: mensaje };
    }
  },

  async obtenerAlertasDashboard(empresaId) {
    try {
      const response = await api.get(`/expediente-laboral/empresa/${empresaId}/alertas-dashboard`);
      return response.data;
    } catch (error) {
      return {
        success: false,
        error: error.response?.data?.error || 'Error al obtener alertas de expediente laboral',
      };
    }
  },

  async descargarZip(empleadoId) {
    try {
      const response = await api.get(`/expediente-laboral/empleado/${empleadoId}/zip`, {
        responseType: 'blob',
      });

      const contentType = response.headers?.['content-type'] || '';
      if (contentType.includes('application/json')) {
        const texto = await response.data.text();
        const parsed = JSON.parse(texto);
        return {
          success: false,
          error: parsed.error || 'No se pudo generar el ZIP',
        };
      }

      const disposition = response.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^"';]+)/i);
      const fileName = match
        ? decodeURIComponent(match[1])
        : `Expediente_${empleadoId}.zip`;

      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return { success: true, fileName };
    } catch (error) {
      let mensaje = 'Error al descargar el expediente';
      const data = error.response?.data;
      if (data instanceof Blob) {
        try {
          const texto = await data.text();
          const parsed = JSON.parse(texto);
          mensaje = parsed.error || mensaje;
        } catch (_) {
          // ignore
        }
      } else if (error.response?.data?.error) {
        mensaje = error.response.data.error;
      }
      return { success: false, error: mensaje };
    }
  },
};

export default expedienteLaboralService;
